import React, { useState } from 'react';
import {
  Pressable, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import Colors from '../../../../../constants/Colors';
import RadioButton from '../../../../../components/RadioButton';
import { UserGroup, UserInstance } from '../../../../../constants/Types';
import Style from '../../../../../constants/Style';
import UserGroupSelection from '../../../../../components/UserGroupSelection';
import AddUserGroupButton from '../../../../../components/AddUserGroupButton';
import { PressableState } from '../../../../../components/PressableStyled';
import { userFullName } from '../../../../../constants/Utils';

// eslint-disable-next-line no-shadow, no-unused-vars
enum Option {
  // eslint-disable-next-line no-unused-vars
  ALL = 'All', PERSON = 'Person', GROUPS = 'Group(s)',
}

interface ResponsibleEntityProps {
  userGroups: UserGroup[],
  users: UserInstance[],
}

const ResponsibleForPPM = ({
  userGroups, users,
}: ResponsibleEntityProps) => {
  const [selectedOption, setSelectedOption] = useState<Option>(Option.ALL);
  const [selectedUser, setSelectedUser] = useState<UserInstance | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<UserGroup[]>([]);
  const [dropdownSelectionIndex, setDropdownSelectionIndex] = useState<number | null>(null);
  const [userDropdownShown, setUserDropdownShown] = useState<boolean>(false);
  const addTagIconDisabled: boolean = selectedGroups.length >= userGroups.length;

  const optionContent = (option: Option) => {
    if (selectedOption !== option) return null;
    if (option === Option.PERSON) {
      return <View>
        <Pressable
          style={styles.userDropdown}
          onPress={() => setUserDropdownShown(!userDropdownShown)}
        >
          <Text style={[Style.dropdownText, Style.dropdownOption]}>
            {userFullName(selectedUser) ?? 'Select a user'}
          </Text>
        </Pressable>
        <View>
          {userDropdownShown && <ScrollView style={Style.dropdownMenu}>
            {(selectedUser ? users : [null, ...users]).map((user: UserInstance | null) => <Pressable
              style={({ hovered, pressed }: PressableState) => [
                Style.dropdownOption,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              onPress={() => {
                setSelectedUser(user);
                setUserDropdownShown(false);
              }}
            >
              <Text style={Style.dropdownText}>
                {userFullName(user) ?? 'Select a user'}
              </Text>
            </Pressable>)}
          </ScrollView>}
        </View>
      </View>;
    }
    if (option === Option.GROUPS) {
      return <View style={styles.groups}>
        {selectedGroups.map((userGroup: UserGroup, index: number) => <UserGroupSelection
          style={{ zIndex: index * -1 }}
          {...{
            userGroups, selectedGroups, dropdownSelectionIndex, setDropdownSelectionIndex, index,
          }}
          selectedGroup={userGroup}
          setSelectedGroup={(newUserGroup: UserGroup) => {
            const newUserGroupList: UserGroup[] = [...selectedGroups];
            newUserGroupList[index] = newUserGroup;
            setSelectedGroups(newUserGroupList);
          }}
          removeGroup={() => {
            const newUserGroupList: UserGroup[] = [...selectedGroups];
            newUserGroupList.splice(index, 1);
            setSelectedGroups(newUserGroupList);
          }}
        />)}
        <AddUserGroupButton
          style={{ zIndex: selectedGroups.length * -1 }}
          disabled={addTagIconDisabled}
          {...{
            userGroups, selectedGroups, dropdownSelectionIndex, setDropdownSelectionIndex,
          }}
          addGroup={(userGroup: UserGroup) => setSelectedGroups([...selectedGroups, userGroup])}
        />
      </View>;
    }
    return null;
  };

  return <>
    <Text style={styles.subtitle}>{'Responsible'}</Text>
    {Object.values(Option).map((option: Option, index: number) => <View
      key={`option_${option}`}
      style={{ zIndex: index * -1 }}
    >
      <RadioButton
        selected={selectedOption === option}
        select={() => setSelectedOption(option)}
        title={option}
      />
      {optionContent(option)}
    </View>)}
  </>;
};

const styles = StyleSheet.create({
  subtitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginBottom: 20,
  },
  groups: {
    marginTop: 8,
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  tag: {
    ...Style.row,
    backgroundColor: Colors.light.hereworksDarkBlue,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginEnd: 8,
    marginVertical: 2,
  },
  tagText: {
    color: '#FFF',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 24,
    marginHorizontal: 4,
  },
  userDropdown: {
    ...Style.dropdown,
    marginTop: 8,
    marginBottom: 4,
  },
});

export default ResponsibleForPPM;
